import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';

// Import global styles
import './assets/styles.scss';

// Chart.js registration
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

// PrimeVue imports
import PrimeVue from 'primevue/config';
import { definePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';

// i18n import
import i18n from './i18n';
import { t } from 'i18next';
import {format, unformat} from "v-money3";
import {route} from "ziggy-js";

// Import loading composable
import { useEnhancedLoading } from './composables/useEnhancedLoading';

window.sellerId = parseInt(import.meta.env.VITE_SELLER_ID || 0)

const appName = import.meta.env.VITE_APP_NAME || 'Linkarme';

createInertiaApp({
    title: (title) => title ? `${title} - ${appName}` : appName,
    resolve: name => {
        const pages = import.meta.glob('./views/**/*.vue', { eager: true });
        const page = pages[`./views/${name}.vue`];


        if (!page) {
            console.error(`Component not found: ${name}`);
            throw new Error(`Page not found: ${name}`);
        }

        return page;
    },
    setup({ el, App, props, plugin }) {
        // Configure PrimeVue Aura theme
        const MyPreset = definePreset(Aura, {
            semantic: {
                primary: {
                    50: '{blue.50}',
                    100: '{blue.100}',
                    200: '{blue.200}',
                    300: '{blue.300}',
                    400: '{blue.400}',
                    500: '{blue.500}',
                    600: '{blue.600}',
                    700: '{blue.700}',
                    800: '{blue.800}',
                    900: '{blue.900}',
                    950: '{blue.950}'
                }
            }
        });

        const _app = i18n(createApp({ render: () => h(App, props) }));

        _app.config.globalProperties.$sellerId = window.sellerId

        _app.config.globalProperties.$filters = {
            capitalize(string) {
                return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
            },
            currency(v, f = {
                prefix: 'R$ ',
                suffix: '',
                thousands: '.',
                decimal: ',',
                precision: 2,
                disableNegative: false,
                disabled: false,
                min: null,
                max: null,
                allowBlank: false,
                minimumNumberOfCharacters: 0,
            }) {
                return format(v, f);
            },
            unformat(v, f = {
                prefix: 'R$ ',
                suffix: '',
                thousands: '.',
                decimal: ',',
                precision: 2,
                disableNegative: false,
                disabled: false,
                min: null,
                max: null,
                allowBlank: false,
                minimumNumberOfCharacters: 0,
            }) {
                return unformat(v, f);
            },
        };

        _app
            .use(plugin)
            .use(PrimeVue, {
                theme: {
                    preset: MyPreset,
                    options: {
                        darkModeSelector: '.app-dark'
                    }
                }
            })
            .use(ToastService)
            .use(ConfirmationService)
            .directive('tooltip', Tooltip)
            .mixin({ methods: { route } })
            .mount(el);
    },
    progress: {
        delay: 250,
        color: '#29d',
        includeCSS: false,
        showSpinner: false,
    },
});

let globalLoadingHandlers = null;

const setupGlobalLoadingHandlers = () => {
    if (globalLoadingHandlers) return globalLoadingHandlers;

    const { showPageLoading, hidePageLoading } = useEnhancedLoading();
    globalLoadingHandlers = { showPageLoading, hidePageLoading };

    router.on('start', (event) => {
        const visit = event.detail.visit;
        const isPartialReload = visit.only && visit.only.length > 0;

        if (!isPartialReload) {
            showPageLoading(t('Loading...'));
        }
    });

    router.on('finish', (event) => {
        const visit = event.detail.visit;
        const isPartialReload = visit.only && visit.only.length > 0;

        if (!isPartialReload) {
            hidePageLoading();
        }
    });

    router.on('error', (event) => {
        hidePageLoading();
    });

    router.on('cancel', (event) => {
        hidePageLoading();
    });

    return globalLoadingHandlers;
};

setupGlobalLoadingHandlers();
